import { load } from './../../../../../grid/gridSmartViews/index.stories';
import { GzTooltip } from '../../../../gzTooltip';
import { DraggableElement, DroppableElement } from '../../../../../utils/dragAndDrop';
import { DOMEventDispatcher } from '../../../../../utils/event.utils';
import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import {
  NodeData,
  SelectedNodes,
  StaticTreeDataSource,
  TreeDataSource,
  TreeDataTransferFormat,
  TreeError,
  TreeEvent,
} from '../../../type';
import { NodeDirectorState, Validation } from '../../../type/validators';
import { NodeTemplateConfig } from '../type';

import { styles } from '../view/styles';
import { DirectiveResult } from 'lit/directive.js';
import { GzLoadMoreNodesButtonWebComponent } from '../../gzLoadMoreNodesButton';

export class GzNodeDirectorWebComponent<T extends object> extends LitElement {
  static get styles() {
    return styles;
  }

  @property({ type: String })
  public nodeId: string;

  @property({ type: Object })
  public dataSource: TreeDataSource;

  @property({ type: Function })
  public nodeRenderer: Function;

  @property({ type: Object })
  public nodes: Map<string, NodeData>;

  @property({ type: Object })
  public node: NodeData;

  @property({ type: Number })
  public depth: number;

  @property({ type: Boolean, attribute: true, reflect: true })
  addCheckbox = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  hideCollapseExpandBtn = false;

  @property({ type: Array, attribute: true, reflect: true })
  selectedNodes: SelectedNodes<T>[] = [];

  @property({ type: Object, attribute: true, reflect: true })
  public translations: { loadMoreNodes: string } = {
    loadMoreNodes: 'LOAD_MORE_NODES_PLACEHOLDER',
  };

  /**
   * This is a shared registry of the flattened elements inside the tree.
   * We receive the shared Map from the parent (gz-tree), then on firstUpdated we register this node’s element under its id
   */
  @property({ attribute: false })
  nodeRefs!: Map<string, HTMLElement>;

  /**
   * Used to handle state storage and change detection from custom node renderers.
   */
  @state()
  private _state: NodeDirectorState;

  @state()
  dragOver = false;

  @query('#loadMoreBtn')
  loadMoreBtn: GzLoadMoreNodesButtonWebComponent;
  
  firstUpdated() {
    if (this.nodeRefs.get(this.nodeId)) {
      return;
    }
    this.nodeRefs.set(this.nodeId, this);
  }

  connectedCallback(): void {
    super.connectedCallback();

    if (this.dataSource?.options?.dragAndDropEnabled) {
      this.attachDragAndDrop();
    }

    if (this.dataSource?.options?.checkbox) {
      this.addCheckbox = true;
    }
  }

  disconnectedCallback(): void {
    this.removeAllListeners();
  }

  public get state(): NodeDirectorState {
    return this._state;
  }

  public setState = (newState: NodeDirectorState | null): void => {
    this._state = newState ? newState : {};
  };

  private removeAllListeners(): void {
    this.replaceWith(this.cloneNode(true));
  }

  private updateSelectedNodesForUi() {
    DOMEventDispatcher.dispatchGlobalEvent(this, TreeEvent.NODE_CHECKED, {
      key: this.node.nodeId,
      value: this.node,
    });
  }

  private attachDragAndDrop(): void {
    new DraggableElement(this)
      .withDragStart(TreeDataTransferFormat.TEXT, () => JSON.stringify([this.node.nodeId]))
      .withDragEnd();

    new DroppableElement(this).withDragOver().withDrop(
      event => this.handleNodeDrop(event),
      () => console.error(TreeError.DROP_NOT_VALID)
    );
  }
  private async handleNodeDrop(event: DragEvent): Promise<void> {
    const nodeIds: string[] = JSON.parse(event.dataTransfer!.getData(TreeDataTransferFormat.TEXT));
    const containerNode = this.nodes.get(this.nodeId)!;

    for (const nodeId of nodeIds) {
      this.dataSource.loading$.next(true);
      const nodeFromStore = this.nodes.get(nodeId)!;

      if (!nodeFromStore) {
        console.error(TreeError.DROPPED_NODE_NOT_SET);
        return;
      }

      if (nodeFromStore.external) {
        await this.handleExternalNodeDrop(nodeFromStore, containerNode);
      } else {
        await this.handleInternalNodeDrop(nodeFromStore, containerNode);
      }
    }

    this.dataSource.loading$.next(false);
  }

  private async handleInternalNodeDrop(nodeFromStore: NodeData, containerNode: NodeData): Promise<void> {
    const validationErrors = await this.dataSource.dropNode(nodeFromStore, containerNode, () =>
      this.dataSource.updateNodeWithNewPosition(nodeFromStore, containerNode.nodeId)
    );

    if (validationErrors) {
      this.emitDropNodeValidationError(nodeFromStore, containerNode, validationErrors);
    }
  }

  private async handleExternalNodeDrop(node: NodeData, containerNode: NodeData): Promise<void> {
    if (node.attachToTree) {
      node.parentNodeId = containerNode.nodeId;
      const validationErrors = await this.dataSource.dropNode(node, containerNode, () =>
        this.dataSource.attachNodeToContainer(node, containerNode)
      );

      this.emitAttachToTreeEvents(validationErrors, node, containerNode);

      return;
    }

    DOMEventDispatcher.dispatchGlobalEvent(this, TreeEvent.EXTERNAL_NODE_DROPPED_WITH_NO_ATTACH, {
      droppedNode: node,
      containerNode,
    });
  }

  private emitAttachToTreeEvents(validationErrors: Validation, node: NodeData, containerNode: NodeData): void {
    if (validationErrors) {
      this.emitDropNodeValidationError(node, containerNode, validationErrors);

      return;
    }

    DOMEventDispatcher.dispatchGlobalEvent(this, TreeEvent.EXTERNAL_NODE_ATTACHED, node);
  }

  private emitDropNodeValidationError(node: NodeData, containerNode: NodeData, validation: Validation): void {
    DOMEventDispatcher.dispatchGlobalEvent(this, TreeEvent.NODE_DROP_VALIDATION_ERROR, {
      node,
      containerNode,
      validation,
    });
  }

  private async nodeToggled(node: NodeData): Promise<void> {
    await this.dataSource.toggleNodeOpen(node);
    DOMEventDispatcher.dispatchGlobalEvent(this, TreeEvent.NODE_TOGGLED, this.node);
  }

  private getChildNodesTemplate(node: NodeData): TemplateResult {
    if (node.isOpen) {
      return html`
        ${repeat(
          node?.childNodeIds || [],
          id => id,
          (id: string) =>
            html` <gz-node-director
               data-level="${this.depth + 1}"
               .nodeRefs=${this.nodeRefs}
              .nodeId=${id}
              .dataSource=${this.dataSource}
              .node=${this.nodes.get(id)}
              .nodes=${this.nodes}
              .depth=${this.depth + 1}
              .selectedNodes="${this.selectedNodes}"
              .nodeRenderer=${this.nodeRenderer}
              .translations=${this.translations}
            ></gz-node-director>
          </div>
            `
        )}
      `;
    }

    return html``;
  }

  renderLoader(): TemplateResult {
    return html`<gz-loader class="node-loader" rows="1"></gz-loader>`;
  }

  private getNodeTemplateConfigs(node: NodeData): NodeTemplateConfig {
    const nodeClassList = classMap({
      'node-container': true,
    });

    const nodeIconClassList = classMap({
      'node-icon': !!node.icon,
      [node.icon!]: !!node.icon,
    });

    const collapseExpandIcon = node?.isOpen ? 'ds-icon-arrow_down_expand' : 'ds-icon-arrow_right_expand';

    const paddingLeft = `--node-padding-left: calc(var(--spacer) * 2.5 * ${this.depth});`;

    return { nodeClassList, collapseExpandIcon, nodeIconClassList, paddingLeft };
  }

  public render(): TemplateResult {
    if (!this.node) {
      return html``;
    }

    const { nodeClassList, collapseExpandIcon, nodeIconClassList, paddingLeft } = this.getNodeTemplateConfigs(
      this.node
    );
    const defaultNodeRendererFn = this.getDefaultNodeRenderer(
      paddingLeft,
      collapseExpandIcon,
      this.node,
      nodeIconClassList
    );
    const customNodeRenderer = this.nodeRenderer && this.nodeRenderer(defaultNodeRendererFn, this);

    if (this.node.isLoading) {
      return this.renderLoader();
    }

    return html`<div class=${nodeClassList}>
      <div class="node-wrapper">${customNodeRenderer || defaultNodeRendererFn()}</div>
      ${this.getChildNodesTemplate(this.node)}
      ${this.node.hasMoreChildNodes && this.node.isOpen
        ? this.renderLoadMoreNodesButton(this.node.isNextPageLoading)
        : nothing}
    </div>`;
  }

  private getCheckedState(elId: string) {
    const index = this.selectedNodes.findIndex(item => item.key === elId);
    return index !== -1;
  }

  private renderLoadMoreNodesButton(isNextPageLoading: boolean = false): TemplateResult {
    return html`<gz-load-more-nodes-button
      .depth=${this.depth}
      .label=${this.translations.loadMoreNodes}
      .isLoading=${isNextPageLoading}
      @click=${this.onLoadMoreNodes}
    ></gz-load-more-nodes-button>`;
  }

  private renderCheckbox() {
    const checked = this.getCheckedState(this.node.nodeId.toString());
    return html`<gz-checkbox
      class="node-check node-name--trim"
      value="${this.node.nodeName.toString()}"
      label="${this.node.nodeName}"
      ?checked="${checked}"
      @change="${this.updateSelectedNodesForUi}"
    ></gz-checkbox>`;
  }

  private getDefaultNodeRenderer(
    paddingLeft: string,
    collapseExpandIcon: string,
    node: NodeData,
    nodeIconClassList: DirectiveResult | undefined
  ): () => TemplateResult {
    const classes = classMap({
      node: true,
      'node--active': !!node?.active,
      'node--drag': this.dragOver,
      'node--checkbox': this.addCheckbox && !node?.noCheckbox,
      'node--disabled': !!node?.isDisabled,
    });

    return (actionTemplate?: TemplateResult) => html`
      <div
        class=${classes}
        style="${paddingLeft}"
        @dragover=${() => (this.dragOver = true)}
        @dragleave=${() => (this.dragOver = false)}
        @drop=${() => (this.dragOver = false)}
      >
        ${this.dragOver ? this.renderDragOverElement() : ''}
        ${!this.showCollapseBtn(this.node)
          ? html`<gz-icon-button
              icon=${collapseExpandIcon}
              buttonType="square"
              buttonFill="quiet"
              buttonColor="secondary"
              size="24"
              fontSize="24"
              @click=${() => this.nodeToggled(this.node)}
            ></gz-icon-button>`
          : html`<span class="collapse-btn-space"></span>`}
        <div class="node-details" @click="${() => this.onNodeClicked(this.node)}">
          ${node.icon ? html`<span class=${nodeIconClassList}></span>` : ''}
          ${this.addCheckbox && !node.noCheckbox ? this.renderCheckbox() : this.renderNodeName()} ${actionTemplate}
        </div>
      </div>
    `;
  }

  private onNodeClicked(node: NodeData): void {
    if (!node.isOpen && !node.isDisabled) {
      this.nodeToggled(node).finally(() => this.requestUpdate());
    }
    DOMEventDispatcher.dispatchGlobalEvent(this, TreeEvent.NODE_CLICKED, node);
    this.handleSettingActiveNode();
  }

  // this method works only for static nodes.
  private showCollapseBtn(node: NodeData): boolean {
    if (this.hideCollapseExpandBtn || node?.isDisabled) {
      return true;
    }

    if (this.dataSource instanceof StaticTreeDataSource) {
      return !node.childNodeIds || node.childNodeIds.length === 0;
    }
    return false;
  }

  private renderNodeName() {
    return html` <div class="name-prefix">
        ${this.node.namePrefixRenderer ? this.node.namePrefixRenderer() : nothing}
      </div>
      <span
        @mouseenter="${this.handleMouseEnter}"
        @mousemove="${GzTooltip.align}"
        @mouseleave="${GzTooltip.hide}"
        class="node-name--trim"
        >${this.node.nodeName}</span
      >`;
  }

  private renderDragOverElement() {
    return html` <div class="drag-over"></div>`;
  }

  private handleMouseEnter(event: MouseEvent): void {
    const item = event.target as HTMLElement;

    if (item.clientWidth !== item.scrollWidth) {
      GzTooltip.show(item.innerText, event);
    }
  }

  private handleSettingActiveNode(): void {
    // nothing should happen when clicking a disabled node
    if (this.node?.isDisabled) {
      return;
    }
    this.dataSource.setNodeActive(this.node, true);
    DOMEventDispatcher.dispatchGlobalEvent(this, TreeEvent.CHANGED_ACTIVE_NODE, this.node);
  }

  private onLoadMoreNodes() {
    this.dataSource.loadPagedNodes(this.node, this.node.loadedPage ? this.node.loadedPage + 1 : 1);
    DOMEventDispatcher.dispatchGlobalEvent(this, TreeEvent.LOAD_NEXT_NODES, this.node);
  }
}
